// page to show batch contest aggregate results
// Importing React and necessary components from Material-UI, MUI icons, and other libraries
import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, Checkbox, Button } from "@mui/material";
import Util from "../../Service/util";
import Service from "../../Service/http";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ReactTable from "react-table-6";
import "react-table-6/react-table.css";
import withFixedColumns from "react-table-hoc-fixed-columns";
import "react-table-hoc-fixed-columns/lib/styles.css";
import { VscCloudDownload } from "react-icons/vsc";
import { CircularProgress } from "@mui/material";
import { FadeLoader } from "react-spinners";

import * as xlsx from "xlsx";

// Applying fixed columns to the ReactTable
const ReactTableFixedColumns = withFixedColumns(ReactTable);
// Icons for checkbox
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Main component for Contest Aggregate Reports
function ContestAggregateReports() {
  // Initializing service and utility functions
  const service = new Service();
  const util = new Util();

  // State variables for managing various aspects of the component
  let [batches, SetBatches] = useState(["All Batches"]);
  let [filteredBatches, setfilteredBatches] = useState([]);
  let [originalFilteredBatches, setOriginalFilteredBatches] = useState([]);
  let [filteredContests, setfilteredContests] = useState([]);
  let [contests, SetContests] = useState([]);
  let [searchContests, SetSearchContests] = useState();
  let [data, SetData] = useState([]);
  let [Page, SetPage] = useState(1);
  let [FinalPage, SetFinalPage] = useState(-1);
  let [PageSize, SetPageSize] = useState(50);
  let [PageSizeOption, SetPageSizeOption] = useState([5, 10, 20, 25, 50, 100]);
  let [QueryButton, SetQueryButton] = useState(false);
  let [QueryText, SetQueryText] = useState("Search Participation");
  let [loading, SetLoading] = useState(false);
  let [dataLoading, SetDataLoading] = useState(false);
  let [downloadData, setDownloadData] = useState([]);

  // Function to build and download CSV file
  function BuildCSV() {
    let jsonData = [];

    originalFilteredBatches?.map((batch) => {
      let tempBatch = { BATCH: batch };

      let tempBatchData = downloadData[batch];

      filteredContests?.map((contest) => {
        tempBatch[contest?.contestName] = tempBatchData[contest?.contestName];
      });

      jsonData.push(tempBatch);
    });

    // Set the time zone to India
    const options = { timeZone: "Asia/Kolkata" };
    // Format the current date
    const formattedDate = new Date().toLocaleDateString("en-IN", options);
    const fileName = "contestAggregate" + formattedDate;

    // Convert JSON to worksheet
    const ws = xlsx.utils.json_to_sheet(jsonData);

    // Create a workbook
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");

    // Save the workbook to a file
    xlsx.writeFile(wb, `${fileName}.xlsx`);
  }

  // Function to debounce input to avoid rapid API calls
  function debounce(func, timeout = 1000) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }
  // Debounced function to handle input changes
  const processChange = debounce((text) => SetSearchContests(text));

  // Function to handle pagination options based on data length
  function PaginationUpdate() {
    let Datalength = data?.length;
    if (Datalength === 0 || Datalength === null) {
      SetPageSizeOption([5, 10, 15, 25, 50]);
    } else {
      let pageOptions = [];
      let defaultOptions = [5, 10, 20, 25, 50, 100];
      defaultOptions.forEach((item) => {
        const cur = Number(item);
        if (cur <= Datalength) {
          pageOptions.push(cur);
        }
      });
      let num = 200;
      while (Number(num) <= Datalength) {
        pageOptions.push(Number(num));
        num += 100;
      }
      if (pageOptions[pageOptions.length - 1] !== Datalength) {
        pageOptions.push(Number(Datalength));
      }
      SetPageSizeOption(pageOptions);
    }
  }

  // Function to adjust page size based on data length
  function PopulatedFill() {
    let Datalength = data?.length;
    if (Page === FinalPage) {
      let cur_size = PageSize;
      if (cur_size === 0) {
        cur_size = 10;
      }
      const res = Datalength % cur_size;
      if (!isNaN(res) && res !== 0) {
        SetPageSize(res);
      }
    }
  }

  // Initial useEffect to fetch batches when the component mounts
  useEffect(() => {
    let isLogin = util.loggedIn();
    let admin = util.isAdmin();
    if (!isLogin && window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
    if (isLogin && admin) {
      service
        .get("api/batches")
        .then((json) => {
          let batches = ["All Batches"];
          json.batches.map((batch) => {
            batches.push(batch);
          });
          SetBatches(batches);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // Function to handle form submission
  const handleSubmit = () => {
    const searchParams = {
      batches: filteredBatches,
      contests: filteredContests,
    };
    if (
      searchParams &&
      searchParams?.batches?.length > 0 &&
      searchParams?.contests?.length > 0
    ) {
      SetDataLoading(true);
      service
        .post("api/contests/participation/aggregate", searchParams)
        .then((json) => {
          setDownloadData(json.finalResult);
          let contests = [];
          let j = 1;
          for (let item in json.finalResult) {
            contests.push({
              no: j++,
              name: item,
              contests: json.finalResult[item],
            });
          }
          SetPageSize(Math.max(10, Math.min(50, contests?.length)));
          SetData(contests);
          setOriginalFilteredBatches(json.batches);
          SetQueryButton(false);
          SetDataLoading(false);
          SetQueryText("Search Participation");
        })
        .catch((error) => {
          SetDataLoading(false);
          console.log(error);
        });
    } else {
      SetData([]);
    }
  };

  const serializedData = encodeURIComponent(JSON.stringify(data));


  // Function to process column data for the ReactTable
  const processColumns = () => {
    let newColumns = [
      {
        Header: "BATCH DETAILS",
        fixed: "left",
        columns: [
          {
            Header: "No",
            accessor: "no",
            width: 50,
            getProps: (state, rowInfo, column) => {
              return {
                style: {
                  background: rowInfo ? "#0F9ADC" : "",
                },
              };
            },
          },
          {
            Header: "Name",
            accessor: "name",
            width: 250,
            Cell: (e) => <a style={{ color: "white" }} target="_blank" href={"/admin/reports/contest?batches=" + e?.value + "&contest=" + encodeURIComponent(JSON.stringify(filteredContests))} > {e.value} </a>,
            getProps: (state, rowInfo, column) => {
              return {
                style: {
                  background: rowInfo ? "#0F9ADC" : "",
                },
              };
            },
          },
        ],
      },
      {
        Header: "CONTESTS",
        columns: filteredContests.map((contest) => {
          return {
            Header: contest?.contestName,
            accessor: `contests.${contest?.contestName}`,
            sortable: true,
            width: 160,
            Cell: (e) => (e ? e.original?.contests[contest?.contestName] : 0),
          };
        }),
      },
    ];

    return newColumns;
  };

  // useEffect to update pagination options when data changes
  useEffect(() => {
    PaginationUpdate();
  }, [data]);

  // useEffect to populate page size based on data length and page
  useEffect(() => {
    PopulatedFill();
  }, [Page, FinalPage]);

  // useEffect to fetch contest data when searchContests changes
  useEffect(() => {
    if (searchContests && searchContests.length > 0) {
      SetLoading(true);
      service
        .get("api/contests/" + searchContests)
        .then((json) => {
          SetContests(json);
          SetLoading(false);
        })
        .catch((error) => {
          console.log(error);
          SetLoading(false);
        });
    } else {
      SetContests([]);
    }
  }, [searchContests]);

  // useEffect to update query button text based on filters
  useEffect(() => {
    if (filteredBatches.length > 0 && filteredContests.length > 0) {
      SetQueryText("Update Participation");
      SetQueryButton(true);
    } else {
      SetQueryText("Search Participation");
      SetQueryButton(false);
    }
  }, [filteredContests, filteredBatches]);

  // Rendering the main component
  return (
    <div
      className=" text-center text-dark h2 "
      style={{ minHeight: "100vh", fontWeight: "revert-layer" }}
    >
      {/* Main title */}
      <p className="mt-4">Contest Summary Participation Report</p>

      {/* Download button */}
      <div
        className="p-0 m-0 h5 text-primary "
        style={{
          textDecoration: "none",
          fontWeight: "600",
          textShadow: "0px 0px #C0C0C0",
        }}
      >
        <VscCloudDownload
          data-bs-toggle="tooltip"
          title="Download Contest Participation Data"
          data-bs-placement="bottom"
          size={40}
          style={{
            cursor: "pointer",
            position: "absolute",
            top: 60,
            right: 10,
            zIndex: 100,
          }}
          onClick={BuildCSV}
        />
      </div>

      {/* Batch and contest selection */}
      <div style={{ verticalAlign: "middle" }}>
        {/* Autocomplete for batches */}
        <Autocomplete
          multiple
          disablePortal
          disableCloseOnSelect
          id="checkboxes-tags-demo"
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          options={
            filteredBatches.includes("All Batches") ? ["All Batches"] : batches
          }
          sx={{
            width: 300,
            display: "inline-block",
            marginRight: 6,
            verticalAlign: "middle",
          }}
          onChange={(event, newValue) => {
            setfilteredBatches([...newValue, "GRAND TOTAL"]);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Batches" />
          )}
        />

        {/* Autocomplete for contests */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) => {
            for (let i = 0; i < filteredContests.length; i++) {
              if (filteredContests[i].contestName === option.contestName)
                return true;
            }
            return false;
          }}
          loading={loading}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.siteName === "CODEFORCES"
                ? option.contestName + " (" + option.contestId + ")"
                : option.contestName}
            </li>
          )}
          options={contests}
          getOptionLabel={(option) =>
            option.siteName === "CODEFORCES"
              ? option.contestName + " (" + option.contestId + ")"
              : option.contestName
          }
          groupBy={(option) => option.siteName}
          sx={{ width: 600, display: "inline-block" }}
          onChange={(event, newValue) => {
            setfilteredContests([
              ...newValue,
              { contestName: "GRAND TOTAL" },
              { contestName: "PARTICIPATION %" },
            ]);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => {
                processChange(e.target.value);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              sx={{
                width: 600,
                display: "inline-block",
                verticalAlign: "middle",
              }}
              label="Select Contests"
            />
          )}
        />

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={!QueryButton}
          variant="contained"
          color="primary"
          style={{
            marginLeft: "36px",
            height: "50px",
            verticalAlign: "middle",
          }}
        >
          {QueryText}
        </Button>
      </div>

      {/* Loading spinner */}
      {dataLoading ? (
        <div
          style={{
            marginTop: "10vh",
            marginRight: "10vh",
            textAlign: "center",
          }}
        >
          <FadeLoader margin={5} color="green" />
        </div>
      ) : null}

      {/* Display ReactTable if data is available */}
      {!dataLoading && data.length > 0 && (
        <ReactTableFixedColumns
          data={data}
          style={{
            textAlign: "center",
            fontWeight: "bolder",
            fontSize: "16px",
            height: "auto",
            marginTop: "20px",
          }}
          pageSize={PageSize}
          pageSizeOptions={PageSizeOption}
          minRows={5}
          loadingText={"Loading Participation Data..."}
          onPageSizeChange={(pageSize, pageIndex) => {
            SetPage(pageIndex);
            SetPageSize(pageSize);
          }}
          onPageChange={(num) => {
            SetPage(num);
          }}
          renderTotalPagesCount={(num) => {
            if (!isFinite(num) || isNaN(num)) {
              num = 1;
            }
            SetFinalPage(num);
            if (num === 0) {
              return 1;
            }
            return num;
          }}
          columns={processColumns()}
        />
      )}
    </div>
  );
}

// Exporting the component as default
export default ContestAggregateReports;
